import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { DrawerLayoutWrapper } from 'common';
import ProgressBar from 'feature/dispatching/components/ProgressBar';
import { formatGallons } from 'utils/helpers/format';
import Compartment from '../components/wizard/Compartment';
import CompartmentAccordion from '../components/wizard/CompartmentAccordion';
import { initialCompartmentState } from '../components/wizard/initialState';

const isPropNil = R.propEq(R.__, null);

const createInitialVolumeState = (compartments, fillAll) =>
  R.map(
    c => ({
      error: false,
      isFetching: false,
      message: `The max volume for this ${fillAll ? 'trailer' : 'compartment'} is ${formatGallons(
        c.capacityInGallons
      )}`,
    }),
    compartments
  );

const Supplier = ({ onClose, next, prev, loadState, setLoadState }) => {
  const { trailer, compartments, fillAll } = loadState;

  const [volumeValidationState, setVolumeValidationState] = useState(
    createInitialVolumeState(compartments, fillAll)
  );

  const setCompartmentVolumeState = R.curry((id, state) =>
    setVolumeValidationState(R.assoc(id, state))
  );

  const isInvalid = () => {
    const compartmentList = R.values(compartments);

    // All Load Types
    if (R.all(isPropNil('loadType'), compartmentList)) return true;

    const activeCompartments = compartmentList.filter(c => {
      return c.loadType?.type !== 'Empty';
    });

    if (R.any(R.propEq('error', true), R.values(volumeValidationState))) return true;
    if (R.any(R.propEq('isFetching', true), R.values(volumeValidationState))) return true;
    if (R.any(R.propEq('volume', ''), activeCompartments)) return true;
    if (R.any(R.propEq('volume', '0'), activeCompartments)) return true;

    const compartment = activeCompartments.find(c => c.capacityInGallons);

    if (R.gt(parseInt(compartment?.volume.replace(/,/g, '')), compartment?.capacityInGallons))
      return true;

    // Custom / House Recipe validation
    const isRecipe = R.complement(R.pathEq(['loadType', 'loadTypeId'], 3));

    const recipes = R.filter(isRecipe, activeCompartments);

    if (R.any(isPropNil('recipe'), recipes)) return true;
    if (R.isEmpty('volume') || R.any(isPropNil('volume'), recipes)) return true;
    if (R.any(isPropNil('destination'), recipes)) return true;

    // Unload validation
    const isUnload = R.pathEq(['loadType', 'loadTypeId'], 3);
    const unloads = R.filter(isUnload, activeCompartments);
    if (R.any(isPropNil('supplier'), unloads)) return true;
    if (R.any(isPropNil('product'), unloads)) return true;

    return false;
  };

  const handleFillAllChange = event => {
    const { checked } = event.target;

    const sumVolume = (total, compartment) => total + compartment.capacityInGallons;
    const fillCompartments = {
      ...initialCompartmentState,
      capacityInGallons: trailer.compartments.reduce(sumVolume, 0),
      compartmentId: 0,
    };
    const indexCompartments = R.compose(
      R.indexBy(R.prop('compartmentId')),
      R.map(R.mergeLeft(initialCompartmentState))
    );
    const defaultCompartments = checked
      ? { 0: fillCompartments }
      : indexCompartments(trailer.compartments);

    setVolumeValidationState(createInitialVolumeState(defaultCompartments));

    setLoadState(
      R.compose(R.assoc('compartments', defaultCompartments), R.assoc('fillAll', checked))
    );
  };

  const buttons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={prev} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid()} onClick={next} sx={{ width: 150 }}>
        Continue
      </Button>
    </Box>
  );

  return (
    <DrawerLayoutWrapper
      buttons={buttons}
      headerButtonText="CLOSE"
      isLoading={false}
      onHeaderButtonClick={onClose}
      title="New Load"
    >
      <ProgressBar label="Supplier (2 of 4)" value={50} />

      <FormControlLabel
        control={<Switch checked={fillAll} onChange={handleFillAllChange} />}
        label="Fill All Compartments"
        sx={{ mt: 2, mx: 2 }}
      />

      <Box sx={{ overflowX: 'hidden' }}>
        {fillAll ? (
          <Box p={3}>
            <Compartment
              compartment={R.prop(0, compartments)}
              loadState={loadState}
              setLoadState={setLoadState}
              setValidationState={setCompartmentVolumeState(0)}
              validationState={R.prop(0, volumeValidationState)}
            />
          </Box>
        ) : (
          Object.values(compartments).map(c => (
            <CompartmentAccordion compartment={c} key={c.compartmentId}>
              <Compartment
                compartment={c}
                loadState={loadState}
                setLoadState={setLoadState}
                setValidationState={setCompartmentVolumeState(c.compartmentId)}
                validationState={R.prop(c.compartmentId, volumeValidationState)}
              />
            </CompartmentAccordion>
          ))
        )}
      </Box>
    </DrawerLayoutWrapper>
  );
};

Supplier.propTypes = {
  loadState: PropTypes.shape({
    compartments: PropTypes.shape({
      location: PropTypes.shape({}),
      recipe: PropTypes.shape({}),
      supplier: PropTypes.shape({}),
    }),
    fillAll: PropTypes.bool,
    location: PropTypes.shape({}),
    trailer: PropTypes.shape({
      compartments: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
  }).isRequired,
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
  setLoadState: PropTypes.func.isRequired,
};

export default Supplier;
